import React from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  ShieldCheck,
  ChevronRight,
  Sparkles,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

/**
 * ============================================================
 * WORKSHOP DETAIL HERO
 * ============================================================
 * Top visual and logistical section of the Workshop Detail page:
 * - Accessible breadcrumb navigation
 * - Category, delivery mode, and status badges
 * - High-impact title in Space Grotesk
 * - Concise short description
 * - Key Information Strip (Date, Time, Duration, Format, Certificate)
 */
export default function WorkshopDetailHero({ workshop }) {
  if (!workshop) return null;

  // Format date for readable presentation (e.g. "Saturday, 24 Oct 2026")
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const parsed = new Date(dateStr);
      if (isNaN(parsed.getTime())) return dateStr;
      return parsed.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* 1. Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center text-xs text-white/50">
        <ol className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <li>
            <Link
              to="/"
              className="hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
            >
              Home
            </Link>
          </li>
          <li aria-hidden="true" className="text-white/30">
            <ChevronRight className="w-3.5 h-3.5" />
          </li>
          <li>
            <Link
              to="/workshops"
              className="hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
            >
              Workshops
            </Link>
          </li>
          <li aria-hidden="true" className="text-white/30">
            <ChevronRight className="w-3.5 h-3.5" />
          </li>
          <li aria-current="page" className="text-white font-medium truncate max-w-[240px] sm:max-w-md">
            {workshop.title}
          </li>
        </ol>
      </nav>

      {/* 2. Badges Strip */}
      <div className="flex flex-wrap items-center gap-2.5 pt-1">
        {/* Category Badge */}
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/15 text-primary border border-primary/30 backdrop-blur-md">
          <Sparkles className="w-3.5 h-3.5" />
          <span>{workshop.category}</span>
        </span>

        {/* Mode Badge */}
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/90 border border-white/15 backdrop-blur-md">
          {workshop.mode === "Online"
            ? "Live Online"
            : workshop.mode === "Offline"
              ? "In-Person"
              : "Hybrid Delivery"}
        </span>

        {/* Pricing Badge */}
        {workshop.is_free ? (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-green-500/15 text-green-400 border border-green-500/30 backdrop-blur-md">
            <span className="text-white/50 line-through font-normal">₹999</span>
            <span>FREE WORKSHOP</span>
          </div>
        ) : (
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30 backdrop-blur-md">
            ₹{workshop.price?.toLocaleString("en-IN")}
          </span>
        )}

        {/* Status indicator: Completed or Cancelled */}

        {(workshop.is_completed || workshop.status === "Completed") && (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/70 border border-white/20">
            <CheckCircle className="w-3.5 h-3.5 text-primary" />
            <span>Completed Session</span>
          </span>
        )}

        {(workshop.status === "Cancelled" || workshop.lifecycle_stage === "cancelled") && (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-destructive/20 text-destructive border border-destructive/40">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Workshop Cancelled</span>
          </span>
        )}
      </div>

      {/* 3. Title */}
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.15] break-words">
        {workshop.title}
      </h1>

      {/* 4. Short Description */}
      <p className="text-base sm:text-lg text-white/75 leading-relaxed max-w-4xl font-normal">
        {workshop.short_description}
      </p>

      {/* 5. Key Information Strip */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-5 sm:p-6 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 divide-y sm:divide-y-0 divide-white/5">
          {/* Date */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-white/50 font-medium">
              <Calendar className="w-3.5 h-3.5 text-primary" />
              <span>Date</span>
            </div>
            <div className="text-sm sm:text-base font-semibold text-white">
              {formatDisplayDate(workshop.date)}
            </div>
          </div>

          {/* Time */}
          <div className="space-y-1 pt-3 sm:pt-0">
            <div className="flex items-center gap-1.5 text-xs text-white/50 font-medium">
              <Clock className="w-3.5 h-3.5 text-primary" />
              <span>Time</span>
            </div>
            <div className="text-sm sm:text-base font-semibold text-white">
              {workshop.start_time} – {workshop.end_time}
            </div>
            <div className="text-[11px] text-white/45">
              {workshop.timezone || "IST (UTC+5:30)"}
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-1 pt-3 sm:pt-0">
            <div className="flex items-center gap-1.5 text-xs text-white/50 font-medium">
              <Clock className="w-3.5 h-3.5 text-primary" />
              <span>Duration</span>
            </div>
            <div className="text-sm sm:text-base font-semibold text-white">
              {workshop.duration}
            </div>
            <div className="text-[11px] text-white/45">Intensive Session</div>
          </div>

          {/* Delivery Format */}
          <div className="space-y-1 pt-3 sm:pt-0">
            <div className="flex items-center gap-1.5 text-xs text-white/50 font-medium">
              <MapPin className="w-3.5 h-3.5 text-primary" />
              <span>Format</span>
            </div>
            <div className="text-sm sm:text-base font-semibold text-white truncate" title={workshop.venue || workshop.mode}>
              {workshop.mode === "Online"
                ? "Virtual Live Session"
                : workshop.venue || workshop.mode}
            </div>
            <div className="text-[11px] text-white/45">
              {workshop.mode === "Online" ? "Interactive Live Stream" : "On-Site / Lab"}
            </div>
          </div>

          {/* Certificate */}
          <div className="space-y-1 pt-3 sm:pt-0 col-span-2 sm:col-span-1">
            <div className="flex items-center gap-1.5 text-xs text-white/50 font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-primary" />
              <span>Credential</span>
            </div>
            <div className="text-sm sm:text-base font-semibold text-white">
              {workshop.certificate_available ? "Certificate Included" : "Certificate N/A"}
            </div>
            <div className="text-[11px] text-white/45">
              {workshop.certificate_available ? "Verified" : "Open Attendance"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
